import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore.js';
import { authService } from '../../services/auth.service.js';
import { canAccessRole } from '../../hooks/useModules.js';

/**
 * Roadmap #98 follow-up — every authenticated route mount kicks a /me
 * fetch so user.permissions stays in sync after an admin grants/revokes
 * a permission. Without this, the persisted zustand store keeps the
 * stale permissions array from login until the user logs out and back in.
 *
 * Throttled to one call per pathname change so navigating around the
 * app doesn't hammer /me on every route render.
 */
export function ProtectedRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    authService
      .me()
      .then((u) => {
        if (u) setUser(u as any);
      })
      .catch(() => {
        // 401 will be handled by the axios interceptor; nothing useful to
        // do here for transient failures.
      });
  }, [isAuthenticated, location.pathname, setUser]);

  if (!isAuthenticated) {
    // Preserve the intended destination (path + query) so the login page can
    // return the user here after auth — required for the OAuth consent hop
    // (/oauth/consent?consent_id=...). Encoded as a `redirect` query param.
    const target = `${location.pathname}${location.search}`;
    const loginUrl =
      target && target !== '/'
        ? `/login?redirect=${encodeURIComponent(target)}`
        : '/login';
    return <Navigate to={loginUrl} replace />;
  }

  return <Outlet />;
}

/**
 * Role-gated layout route (gh#1565), nested INSIDE ProtectedRoute: a user below `requiredRole`
 * is redirected before the page shell mounts. Defense-in-depth only — the API stays the boundary.
 */
export function RoleRoute({ requiredRole }: { requiredRole: string }) {
  const userRole = useAuthStore((s) => s.user?.role ?? 'guest');
  if (!canAccessRole(requiredRole, userRole)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}
